import {
  CALL_THROUGH_NOTICE,
  callPlan,
  formatPhone,
  type CallingSource,
} from "@roof-hawk/core";
import { useEffect, useState } from "react";

import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { fetchCallingSources, placeCall } from "../../lib/queries";
import { useSession } from "../../session";

/**
 * The Call button (ADR 0310).
 *
 * Jonathon, 2026-10-24: a call button on every customer "so we don't have to
 * copy and paste their number to call". What it does is decided in
 * packages/core (`callPlan`), not here: a job from a source marked for it
 * rings this person's own mobile through Roof Board and connects the
 * customer, recorded and transcribed; every other job opens the phone's
 * dialler with the number in it, from the rep's own number, as those calls
 * were always made.
 *
 * The two look the same on purpose. A rep should press Call and talk, not
 * work out which kind of call this is. The only difference they see is the
 * line that appears while Roof Board rings them.
 */
export function CallButton({
  number,
  leadSource,
  jobId,
  contactId,
  compact = false,
  line,
}: {
  number: string | null | undefined;
  leadSource: string | null | undefined;
  jobId?: string | null;
  contactId?: string | null;
  /** A small button beside a number rather than a full one in a header. */
  compact?: boolean;
  /** The line to call from, as the phone bubble's picker chose it (ADR 0321);
   * null means the rep's own. */
  line?: string | null;
}) {
  const { profile } = useSession();
  const sources = useCallingSources();
  const [state, setState] = useState<"idle" | "ringing" | "connected">("idle");
  const [error, setError] = useState<string | null>(null);

  const plan = callPlan({
    number,
    leadSource,
    sources: sources ?? [],
    caller: profile,
  });

  // The line under the button goes away on its own; a call that is under way
  // has nothing more to say here.
  useEffect(() => {
    if (state !== "connected") return;
    const timer = setTimeout(() => setState("idle"), 8000);
    return () => clearTimeout(timer);
  }, [state]);

  if (plan.kind === "none") return null;

  const className = compact ? "call-btn compact" : "call-btn";
  const label = compact
    ? t("Call")
    : t("Call {number}", { number: formatPhone(number) ?? "" }).trim();

  // Until the sources are known, a person who places calls cannot tell which
  // kind this is, and a tap would dial a Thumbtack customer from their own
  // phone, unrecorded. A moment's wait instead.
  if (sources === null && profile?.places_calls === true) {
    return (
      <span className={`${className} waiting`} aria-disabled="true">
        <PhoneIcon /> {label}
      </span>
    );
  }

  if (plan.kind === "own-phone") {
    return (
      <a
        className={className}
        href={plan.href}
        title={t("Call from your phone")}
      >
        <PhoneIcon /> {label}
      </a>
    );
  }

  const to = plan.to;
  async function ring() {
    setError(null);
    setState("ringing");
    try {
      await placeCall(callPayload(to, jobId, contactId, line));
      setState("connected");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setState("idle");
    }
  }

  return (
    <span className="call-wrap">
      <button
        type="button"
        className={className}
        disabled={state === "ringing"}
        title={t(
          "Call through Roof Board: your phone rings, then the customer is connected",
        )}
        onClick={() => void ring()}
      >
        <PhoneIcon /> {state === "ringing" ? t("Ringing you…") : label}
      </button>
      {state !== "idle" && !compact ? (
        <span className="call-note">{t(CALL_THROUGH_NOTICE)}</span>
      ) : null}
      {error ? <span className="call-note err">{error}</span> : null}
    </span>
  );
}

/** What `twilio-call` is told: the number, the job, and the line if one was
 * picked (ADR 0321) — left out otherwise, so the rep's own is used. */
function callPayload(
  to: string,
  jobId: string | null | undefined,
  contactId: string | null | undefined,
  line: string | null | undefined,
): Parameters<typeof placeCall>[0] {
  const payload: Parameters<typeof placeCall>[0] = {
    to,
    jobId: jobId ?? null,
    contactId: contactId ?? null,
  };
  if (line) payload.from = line;
  return payload;
}

/**
 * The lead sources' calling rule, read once per app load and shared by every
 * button on the screen. Forty rows that change when Jonathon says so.
 */
let cached: CallingSource[] | null = null;
let pending: Promise<CallingSource[]> | null = null;

function loadCallingSources(): Promise<CallingSource[]> {
  if (cached) return Promise.resolve(cached);
  // A failure is forgotten, so the next button to mount asks again rather than
  // every button dialling from the rep's own phone until the page reloads.
  pending ??= fetchCallingSources().then(
    (rows) => {
      cached = rows;
      pending = null;
      return rows;
    },
    (error: unknown) => {
      pending = null;
      throw error;
    },
  );
  return pending;
}

/** The sources, or null while they are still being asked for, or [] if the
 * asking failed — then every job dials from the rep's own phone, as before. */
function useCallingSources(): CallingSource[] | null {
  const loaded = useAsync(loadCallingSources, []);
  if (loaded.error) return [];
  return loaded.data ?? cached;
}

/** For tests, and for the Tools screen once it edits the flag. */
export function forgetCallingSources(): void {
  cached = null;
  pending = null;
}

function PhoneIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}
